import { useEffect, useRef, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebaseConfig';
import { authenticatePublishingWallet } from '../utils/digitalAssetsClient';
import { getPaymentNetwork } from '../utils/paymentNetwork';

let callable;
function readPending(key) {
    try { const value = JSON.parse(localStorage.getItem(key)); return value?.requestId && value?.txSig ? value : null; }
    catch { return null; }
}
export function usePaymentReceipt({ wallet, connection, kind, spaceOwnerUid, componentId }) {
    const uid = wallet.publicKey?.toBase58?.() || '';
    const scope = `muulti-payment:${uid}:${kind}:${spaceOwnerUid}:${componentId}`;
    const current = useRef({});
    current.current = { uid, scope, wallet, connection };
    const [state, setState] = useState(() => ({ scope, pending: readPending(scope), error: '', verifying: false }));
    const [network, setNetwork] = useState({ connection: null, cluster: '' });
    useEffect(() => {
        let active = true;
        getPaymentNetwork(connection).then(cluster => { if (active) setNetwork({ connection, cluster }); }).catch(() => {});
        return () => { active = false; };
    }, [connection]);
    useEffect(() => { setState({ scope, pending: readPending(scope), error: '', verifying: false }); }, [scope]);
    const isCurrent = capture => current.current.uid === capture.uid && current.current.scope === capture.scope &&
        current.current.wallet.connected && current.current.connection === capture.connection;
    const call = async (capture, action, payload) => {
        await authenticatePublishingWallet(capture.wallet, { isCurrent: () => isCurrent(capture) });
        if (!callable) callable = httpsCallable(functions, 'payments', { timeout: 120000 });
        const result = (await callable({ ...payload, action })).data;
        if (!isCurrent(capture)) throw new Error('Wallet changed. Please retry.');
        return result;
    };
    const remember = (intent, txSig) => {
        const capture = current.current;
        const pending = { requestId: intent.requestId, txSig, cluster: intent.cluster, payer: intent.payer };
        try { localStorage.setItem(scope, JSON.stringify(pending)); } catch { /* The current page still retains recovery state. */ }
        if (intent.payer === capture.uid && capture.scope === scope) setState({ scope, pending, error: '', verifying: false });
        return pending;
    };
    const prepare = async ({ amount, amountRaw, recipient, currency, tokenMint = '', expectedVersion, targetId = '' }) => {
        const capture = { ...current.current };
        if (readPending(scope) || (state.scope === scope && state.pending)) throw new Error('Verify the submitted payment before sending another payment.');
        const cluster = await getPaymentNetwork(connection);
        const intent = await call(capture, 'prepare', { kind, spaceOwnerUid, componentId: String(componentId), targetId, amount, cluster, expectedVersion });
        if (intent.recipient !== recipient || intent.currency !== currency || (intent.tokenMint || '') !== tokenMint ||
            intent.quote?.baseAmountAtomic !== amountRaw || intent.payer !== uid) throw new Error('The published payment settings changed. Reload before paying.');
        return intent;
    };
    const verify = async (submitted = null) => {
        const capture = { ...current.current };
        const pending = submitted || (state.scope === scope && state.pending) || readPending(scope);
        if (!pending) throw new Error('No submitted payment needs verification.');
        if (pending.payer !== capture.uid || capture.scope !== scope) throw new Error('Wallet changed. Please retry.');
        setState({ scope, pending, error: '', verifying: true });
        try {
            const result = await call(capture, 'confirm', pending);
            if (result.status !== 'confirmed') throw new Error('Payment verification is still pending.');
            try { localStorage.removeItem(scope); } catch { /* No persistent recovery state was available. */ }
            setState({ scope, pending: null, error: '', verifying: false });
            window.dispatchEvent(new Event('muulti-payment-confirmed'));
            return result;
        } catch (error) {
            if (isCurrent(capture)) {
                if (error.details?.transactionFailed) {
                    try { localStorage.removeItem(scope); } catch { /* Keep current state below. */ }
                }
                setState({ scope, pending: error.details?.transactionFailed ? null : pending, error: error.message || 'Unable to verify payment.', verifying: false });
            }
            throw error;
        }
    };
    return { prepare, remember, verify, cluster: network.connection === connection ? network.cluster : '',
        ...(state.scope === scope ? state : { pending: readPending(scope), error: '', verifying: false }) };
}
